import deviceCapabilityDetector from './deviceCapabilityDetector';

export const DEVICE_ID_STORAGE_KEY = 'msales_device_id';

let memoryDeviceId = null;

const safeReadLocalStorage = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    return null;
  }
};

const safeWriteLocalStorage = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch (error) {}
};

const createRandomPart = () => {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID().replace(/-/g, '');
  }

  if (window.crypto?.getRandomValues) {
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes)
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('');
  }

  return `${Date.now().toString(36)}${Math.random()
    .toString(36)
    .slice(2, 18)}`;
};

export const createDeviceId = () =>
  `pwa_${Date.now().toString(36)}_${createRandomPart()}`;

export const getOrCreateDeviceId = () => {
  const storedDeviceId = safeReadLocalStorage(DEVICE_ID_STORAGE_KEY);
  if (storedDeviceId) {
    memoryDeviceId = storedDeviceId;
    return storedDeviceId;
  }

  if (!memoryDeviceId) {
    memoryDeviceId = createDeviceId();
  }

  safeWriteLocalStorage(DEVICE_ID_STORAGE_KEY, memoryDeviceId);
  return memoryDeviceId;
};

const getConnection = () =>
  navigator.connection || navigator.mozConnection || navigator.webkitConnection || null;

const getPlatform = () => {
  const hardware = deviceCapabilityDetector.deviceInfo?.hardware || {};
  const isStandalone =
    window.matchMedia?.('(display-mode: standalone)')?.matches ||
    window.navigator?.standalone;

  if (hardware.isAndroid) return isStandalone ? 'android-pwa' : 'android-web';
  if (hardware.isIOS) return isStandalone ? 'ios-pwa' : 'ios-web';
  return isStandalone ? 'pwa' : 'web';
};

const getEstimatedRam = () => {
  if (navigator.deviceMemory) {
    return `${navigator.deviceMemory}GB`;
  }

  return deviceCapabilityDetector.deviceInfo?.memory?.estimatedDeviceRAM || 'unknown';
};

export const getDeviceSnapshot = () => {
  const deviceInfo = deviceCapabilityDetector.deviceInfo || {};
  const hardware = deviceInfo.hardware || {};
  const performanceInfo = deviceInfo.performance || {};
  const connection = getConnection();
  const tier = deviceInfo.tier || 'UNKNOWN';

  return {
    appVerzija: global.appVersion || process.env.REACT_APP_VERSION || 'unknown',
    platforma: getPlatform(),
    browserUa: navigator.userAgent || '',
    ekran:
      hardware.screenSize ||
      `${window.screen?.width || window.innerWidth}x${
        window.screen?.height || window.innerHeight
      }`,
    pixelRatio: hardware.pixelRatio || window.devicePixelRatio || 1,
    cpuJezgra: performanceInfo.cores || navigator.hardwareConcurrency || null,
    ramProcjena: getEstimatedRam(),
    veza: connection?.effectiveType || connection?.type || 'unknown',
    tier,
    slabijiUredjaj:
      tier === 'LOW_END' || hardware.isLowEndDevice ? 'D' : 'N',
  };
};

export const buildDeviceRegistryPayload = () => ({
  metadata: {
    deviceId: getOrCreateDeviceId(),
    deviceSnapshot: getDeviceSnapshot(),
  },
});

const getApiBaseUrl = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  if (apiUrl) {
    return apiUrl.replace(/\/$/, '');
  }

  const graphqlUrl = process.env.REACT_APP_GRAPHQL_SERVER;
  if (graphqlUrl) {
    return graphqlUrl.replace(/\/graphql\/?$/, '').replace(/\/$/, '');
  }

  return '';
};

export const touchDeviceRegistry = async (idToken) => {
  if (!idToken || navigator.onLine === false) {
    return null;
  }

  const controller =
    typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timeoutId = controller
    ? setTimeout(() => controller.abort(), 8000)
    : null;

  try {
    const response = await fetch(
      `${getApiBaseUrl()}/api/public/device-registry/touch`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(buildDeviceRegistryPayload()),
        signal: controller?.signal,
      },
    );

    const data = await response.json().catch(() => null);

    if (!response.ok) {
      const message =
        data?.message || data?.error || `Device registry touch failed (${response.status})`;
      throw new Error(message);
    }

    return data;
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
};
